import type { Feature, FeatureCollection } from "geojson";
import { Drone } from "lucide-react";
import { type ComponentType, useMemo } from "react";

import type { TerrainReport } from "../../api/types";
import { useUi } from "../../state/ui";
import { circle, distanceM, offset, type LonLat } from "../graphics/geometry";
import { t } from "../../i18n/i18n";
import { terrainDroneFeatures } from "../drones/dronesFeatures";
import { revealPaint, revealStyle, useFadeIn } from "../reveal";
import { FRIEND_BLUE } from "../units/symbols";

/** Produits de l'analyse du terrain sur la carte (registre H), aux couleurs de la symbologie OTAN :
 *  - praticabilité façon MCOO : hachures simples = SLOW (restreint), croisillons = NO-GO (très restreint), GO sans
 *    marque, en vert, la couleur APP-6 des obstacles (l'iPad : orange et rouge, le rouge étant celui de
 *    l'ennemi) ;
 *  - terrain vu du point haut principal en points bleu ami ; points hauts en triangles à l'encre, étiquetés ;
 *  - goulots de la zone en vert selon la sévérité ;
 *  - axe favorable en bleu ami tireté (mesure amie proposée : état « prévu » en APP-6), portées d'engagement
 *    ennemies en rouge tireté, tronçons exposés soulignés de rouge ;
 *  - zone analysée en tireté bleu ami ;
 *  - éléments drones de l'analyse (chantier « Drones », calque « Éléments drones de l'analyse du terrain », décoché
 *    par défaut) : zones de mise en œuvre numérotées en bleu ami, cellules masquées à nos drones par le relief en
 *    ambre.
 *  Couches insérées sous les graphiques tactiques et les unités. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
}

export const OBSTACLE_GREEN = "#008A2E";
const HOSTILE_RED = "#C83E3E";
const BELOW = "graphics-fill";
const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
const SEVERITY_GREEN = ["match", ["get", "severity"], "HIGH", "#006B24", "MEDIUM", "#1E9C45", "#6CC287"];
/** Ambre des cellules masquées à nos drones (risque de perte de liaison par le relief). */
export const LINK_LOSS_AMBER = "#B26A00";
/** Fondu des produits de l'analyse (apparition d'un rapport, œil du panneau). */
const FADE_MS = 450;

function collection(features: Feature[]): FeatureCollection {
  return { type: "FeatureCollection", features };
}

function bearing(a: LonLat, b: LonLat): number {
  const scale = Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180);
  return ((Math.atan2((b[0] - a[0]) * scale, b[1] - a[1]) * 180) / Math.PI + 360) % 360;
}

/** Chevron de l'axe à son dernier point (branches rabattues de 150°, comme l'axe d'effort). */
export function chevron(coords: LonLat[], size: number): LonLat[] | null {
  if (coords.length < 2) return null;
  const tip = coords[coords.length - 1]!;
  // Cap moyen sur les derniers ~300 m : le lissage rend le dernier segment très court.
  let back = coords.length - 2;
  while (back > 0 && distanceM(coords[back]!, tip) < 300) back -= 1;
  const heading = bearing(coords[back]!, tip);
  return [offset(tip, size, heading - 150), tip, offset(tip, size, heading + 150)];
}

export function buildTerrainFeatures(report: TerrainReport | null) {
  if (!report) return null;
  const hLat = (report.cell_span.dlat / 2) * 0.94;
  const hLon = (report.cell_span.dlon / 2) * 0.94;
  const cells = collection(
    report.cells
      .filter((c) => c.cls !== "go")
      .map((c) => ({
        type: "Feature",
        properties: { cls: c.cls },
        geometry: {
          type: "Polygon",
          coordinates: [[
            [c.lon - hLon, c.lat - hLat],
            [c.lon + hLon, c.lat - hLat],
            [c.lon + hLon, c.lat + hLat],
            [c.lon - hLon, c.lat + hLat],
            [c.lon - hLon, c.lat - hLat],
          ]],
        },
      })),
  );
  const ring = [...report.polygon, report.polygon[0]!];
  const zone = collection([{ type: "Feature", properties: { camp: report.camp }, geometry: { type: "Polygon", coordinates: [ring] } }]);
  const seen = collection(report.seen.map((p) => ({ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: p } })));
  const chokepoints = collection(
    report.obstacles.chokepoints.map((c) => ({
      type: "Feature",
      properties: { severity: c.severity },
      geometry: { type: "Point", coordinates: [c.lon, c.lat] },
    })),
  );
  const axis = report.axis ?? null;
  const axisFeatures: Feature[] = [];
  const rings: Feature[] = [];
  if (axis) {
    for (const run of axis.runs) {
      axisFeatures.push({ type: "Feature", properties: { level: run.level }, geometry: { type: "LineString", coordinates: run.coords } });
    }
    const all = axis.runs.flatMap((r) => r.coords) as LonLat[];
    const head = chevron(all, 400);
    if (head) axisFeatures.push({ type: "Feature", properties: { level: 0, head: true }, geometry: { type: "LineString", coordinates: head } });
    for (const t of report.threat_rings) {
      rings.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: circle([t.lon, t.lat], t.range_m, 72) } });
    }
  }
  return { cells, zone, seen, chokepoints, axis: collection(axisFeatures), rings: collection(rings) };
}

/** Étiquette d'un point haut : puce de l'interface, triangle à l'encre, nom et altitude. */
function HighPointTag({ name, elevation }: { name: string | null; elevation: number }) {
  return (
    <div className="pointer-events-none flex h-5 items-center gap-1.5 rounded-md border border-line-strong bg-surface px-1.5 font-sans whitespace-nowrap shadow-[0_1px_2px_rgb(16_18_24/0.12)]">
      <span className="text-[10px] leading-none text-ink">▲</span>
      <span className="text-[12px] leading-none font-semibold text-ink">{name ?? t("Point haut")}</span>
      <span className="font-mono text-[11px] leading-none text-ink-soft tabular-nums">{elevation} m</span>
    </div>
  );
}

/** Repère d'une zone de mise en œuvre de nos drones : puce de l'interface, drone en bleu ami et numéro. */
function LaunchSiteTag({ n, name }: { n: number; name: string }) {
  return (
    <div className="pointer-events-none flex h-5 items-center gap-1 rounded-md border px-1.5 font-sans whitespace-nowrap shadow-[0_1px_2px_rgb(16_18_24/0.12)]" style={{ borderColor: FRIEND_BLUE, background: "var(--color-surface)" }}>
      <Drone size={12} style={{ color: FRIEND_BLUE }} aria-hidden />
      <span className="font-mono text-[12px] leading-none font-bold" style={{ color: FRIEND_BLUE }}>{n}</span>
      {name && <span className="text-[11px] leading-none text-ink-soft">{name}</span>}
    </div>
  );
}

export function TerrainLayers({ Source, Layer, Marker }: EngineParts) {
  const report = useUi((s) => s.terrain);
  const overlay = useUi((s) => s.terrainOverlay);
  const droneTerrain = useUi((s) => s.controlVisible.droneTerrain === true);
  const built = useMemo(() => buildTerrainFeatures(report), [report]);
  const drones = useMemo(() => (droneTerrain ? terrainDroneFeatures(report) : null), [report, droneTerrain]);
  // Nouveau rapport : ses couches montent en fondu ; l'œil du panneau estompe la praticabilité et l'observation.
  const shown = useFadeIn(report);
  // Éléments drones : en fondu à chaque fois que la ligne du panneau Calques les montre.
  const dronesShown = useFadeIn(drones ? report : null);
  if (!report || !built) return null;
  const fade = (type: string, paint: Record<string, unknown>, on = true) => revealPaint(type, paint, shown && on, FADE_MS);

  return (
    <>
      <Source id="gx-terrain-cells" type="geojson" data={built.cells}>
        <Layer id="terrain-slow" type="fill" beforeId={BELOW} filter={["==", ["get", "cls"], "slow"]}
          paint={fade("fill", { "fill-pattern": "mcoo-slow" }, overlay)} />
        <Layer id="terrain-nogo" type="fill" beforeId={BELOW} filter={["==", ["get", "cls"], "no_go"]}
          paint={fade("fill", { "fill-pattern": "mcoo-nogo" }, overlay)} />
      </Source>
      <Source id="gx-terrain-seen" type="geojson" data={built.seen}>
        <Layer id="terrain-seen" type="circle" beforeId={BELOW}
          paint={fade("circle", { "circle-radius": 2.5, "circle-color": FRIEND_BLUE, "circle-opacity": 0.8,
            "circle-stroke-color": "#ffffff", "circle-stroke-width": 0.5 }, overlay)} />
      </Source>
      <Source id="gx-terrain-chokepoints" type="geojson" data={built.chokepoints}>
        <Layer id="terrain-chokepoints" type="circle" beforeId={BELOW}
          paint={fade("circle", { "circle-radius": ["match", ["get", "severity"], "HIGH", 6, "MEDIUM", 5, 4], "circle-color": SEVERITY_GREEN,
            "circle-stroke-color": "#ffffff", "circle-stroke-width": 1.5 })} />
      </Source>
      <Source id="gx-terrain-rings" type="geojson" data={built.rings ?? EMPTY}>
        <Layer id="terrain-threat-rings" type="line" beforeId={BELOW}
          paint={fade("line", { "line-color": HOSTILE_RED, "line-width": 1.5, "line-dasharray": [3, 2], "line-opacity": 0.85 })} />
      </Source>
      <Source id="gx-terrain-axis" type="geojson" data={built.axis}>
        {/* Tronçons exposés à la menace ennemie : soulignés de rouge (portée ENI). */}
        <Layer id="terrain-axis-exposure" type="line" beforeId={BELOW} filter={[">=", ["get", "level"], 1]}
          layout={{ "line-cap": "round", "line-join": "round" }}
          paint={fade("line", { "line-color": HOSTILE_RED, "line-width": ["match", ["get", "level"], 2, 14, 10], "line-opacity": 0.35 })} />
        <Layer id="terrain-axis-casing" type="line" beforeId={BELOW} layout={{ "line-cap": "round", "line-join": "round" }}
          paint={fade("line", { "line-color": "#ffffff", "line-width": 7 })} />
        {/* Tireté : un axe proposé (état « prévu » en APP-6), distinct des limites du fuseau en trait plein. */}
        <Layer id="terrain-axis" type="line" beforeId={BELOW} layout={{ "line-join": "round" }}
          paint={fade("line", { "line-color": FRIEND_BLUE, "line-width": 4, "line-dasharray": [2.5, 1.2] })} />
      </Source>
      <Source id="gx-terrain-zone" type="geojson" data={built.zone}>
        <Layer id="terrain-zone-fill" type="fill" beforeId={BELOW} filter={["!", ["get", "camp"]]}
          paint={fade("fill", { "fill-color": FRIEND_BLUE, "fill-opacity": 0.06 })} />
        <Layer id="terrain-zone-outline" type="line" beforeId={BELOW}
          paint={fade("line", { "line-color": FRIEND_BLUE, "line-width": 2.5, "line-dasharray": [2, 1.5] })} />
      </Source>
      {drones && (
        <Source id="gx-terrain-drone-masked" type="geojson" data={drones.masked}>
          <Layer id="terrain-drone-masked" type="fill" beforeId={BELOW}
            paint={revealPaint("fill", { "fill-color": LINK_LOSS_AMBER, "fill-opacity": 0.22 }, dronesShown, FADE_MS)} />
        </Source>
      )}
      {drones &&
        (report.drones?.launch_sites ?? []).map((site, i) => (
          <Marker key={`drone-${site.lon},${site.lat}`} longitude={site.lon} latitude={site.lat} anchor="center"
            style={{ pointerEvents: "none", ...revealStyle(dronesShown, FADE_MS) }}>
            <LaunchSiteTag n={i + 1} name={site.name} />
          </Marker>
        ))}
      {(report.relief?.high_points ?? []).map((hp) => (
        <Marker key={`${hp.lon},${hp.lat}`} longitude={hp.lon} latitude={hp.lat} anchor="bottom" offset={[0, -4]}
          style={{ pointerEvents: "none", ...revealStyle(shown, FADE_MS) }}>
          <HighPointTag name={hp.name} elevation={hp.elevation_m} />
        </Marker>
      ))}
    </>
  );
}

/** Motifs MCOO (tuile 12 px) : fond vert léger, hachures simples (SLOW) ou croisées (NO-GO). */
export function provideHatchImage(
  map: { hasImage(id: string): boolean; addImage(id: string, image: ImageData, options?: { pixelRatio?: number }): void },
  id: string,
): boolean {
  if (id !== "mcoo-slow" && id !== "mcoo-nogo") return false;
  if (map.hasImage(id)) return true;
  const size = 24;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  const cross = id === "mcoo-nogo";
  ctx.fillStyle = cross ? "rgba(0, 138, 46, 0.16)" : "rgba(0, 138, 46, 0.10)";
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = "rgba(0, 138, 46, 0.9)";
  ctx.lineWidth = 2.5;
  ctx.lineCap = "square";
  const diagonal = (x0: number, y0: number, x1: number, y1: number) => {
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  };
  // Diagonales prolongées au-delà de la tuile : raccord sans couture entre tuiles voisines.
  diagonal(-4, size + 4, size + 4, -4);
  diagonal(-4, 4, 4, -4);
  diagonal(size - 4, size + 4, size + 4, size - 4);
  if (cross) {
    diagonal(-4, -4, size + 4, size + 4);
    diagonal(size - 4, -4, size + 4, 4);
    diagonal(-4, size - 4, 4, size + 4);
  }
  map.addImage(id, ctx.getImageData(0, 0, size, size), { pixelRatio: 2 });
  return true;
}

